import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import Footer from '../offer-package/footer';
import Header from '../offer-package/header';
import { StyledLink } from '../ui';

export type SpecialPackageUnavailableKind =
  | 'inactive'
  | 'no-rooms'
  | 'generic';

export interface ISpecialPackageUnavailableStateProps {
  language: string;
  kind: SpecialPackageUnavailableKind;
  title: string;
  description: string;
  arrival?: string;
  departure?: string;
}

export function SpecialPackageUnavailableState(
  props: ISpecialPackageUnavailableStateProps
) {
  const { t } = useTranslation('offer-package');

  const formatDate = (value?: string) => {
    if (!value) {
      return null;
    }

    const date = parseISO(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(
      props.language === 'de' ? 'de-AT' : 'en-US',
      {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }
    ).format(date);
  };

  const formattedArrival = formatDate(props.arrival);
  const formattedDeparture = formatDate(props.departure);

  return (
    <div>
      <Header></Header>

      <main className="container max-w-5xl px-2 sm:px-0 pb-24">
        <div className="pt-10">
          <StyledLink href={`/${props.language}/special`}>
            {t('special.unavailable.actions.back')}
          </StyledLink>
        </div>

        <section className="relative mt-6 overflow-hidden rounded-[2rem] border border-brand-green-200 bg-[linear-gradient(135deg,#f7efe1_0%,#ffffff_45%,#edf7f2_100%)] px-6 py-10 shadow-sm sm:px-10 sm:py-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(30,98,72,0.12),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(214,162,96,0.18),transparent_30%)]"></div>

          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.8fr)] lg:items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-green-700">
                {t('special.unavailable.eyebrow')}
              </p>

              <h1 className="mt-4 font-serif text-4xl leading-tight text-brand-cashmere-900 sm:text-5xl">
                {props.title}
              </h1>

              <p className="mt-4 max-w-2xl text-lg leading-8 text-brand-cashmere-800">
                {props.description}
              </p>

              {formattedArrival && formattedDeparture && (
                <div className="mt-6 inline-flex rounded-full border border-brand-green-200 bg-white/80 px-5 py-3 text-sm text-brand-green-900 shadow-sm">
                  <span className="font-medium">{t('special.unavailable.period')}</span>
                  <span className="ml-2">
                    {formattedArrival} - {formattedDeparture}
                  </span>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <StyledLink
                  href={`/${props.language}/special`}
                  design="primary"
                >
                  {t('special.unavailable.actions.browse')}
                </StyledLink>

                <StyledLink href="mailto:info@mandira-ayurveda.at">
                  {t('special.unavailable.actions.email')}
                </StyledLink>

                <StyledLink href="tel:+4333332801">
                  {t('special.unavailable.actions.call')}
                </StyledLink>
              </div>
            </div>

            <aside className="rounded-[1.75rem] border border-white/70 bg-white/80 p-6 shadow-sm backdrop-blur">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-green-700">
                {t('special.unavailable.sidebar.title')}
              </p>

              <div className="mt-4 space-y-4 text-sm leading-7 text-slate-700">
                <div>
                  <p className="font-semibold text-slate-900">
                    {t('special.unavailable.sidebar.explore-title')}
                  </p>
                  <p>{t('special.unavailable.sidebar.explore-description')}</p>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">
                    {t('special.unavailable.sidebar.support-title')}
                  </p>
                  <p>{t('special.unavailable.sidebar.support-description')}</p>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">
                    {props.kind === 'inactive'
                      ? t('special.unavailable.sidebar.inactive-title')
                      : t('special.unavailable.sidebar.dynamic-title')}
                  </p>
                  <p>
                    {props.kind === 'inactive'
                      ? t('special.unavailable.sidebar.inactive-description')
                      : t('special.unavailable.sidebar.dynamic-description')}
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <Footer></Footer>
    </div>
  );
}

export default SpecialPackageUnavailableState;
